import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface QuickStatsCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  isLoading?: boolean;
  className?: string;
  iconClassName?: string;
}

const QuickStatsCard: React.FC<QuickStatsCardProps> = ({
  title,
  value,
  description,
  icon: Icon,
  trend,
  isLoading,
  className,
  iconClassName,
}) => {
  if (isLoading) {
    return (
      <Card className={cn("relative overflow-hidden", className)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
          <CardTitle className="text-xs sm:text-sm font-medium">{title}</CardTitle>
          <Skeleton className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg" />
        </CardHeader>
        <CardContent className="pt-2">
          <Skeleton className="h-6 sm:h-8 w-24 sm:w-32 mb-1" />
          {description && <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("relative overflow-hidden", className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
        <CardTitle className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">{title}</CardTitle>
        <div className={cn("h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center flex-shrink-0 ml-2", iconClassName)}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight">{value}</div>
        {description && (
          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-500 mt-0.5 sm:mt-1 truncate">{description}</p>
        )}
        {trend && (
          <div className={cn(
            "text-[10px] sm:text-xs font-medium mt-1 sm:mt-2 flex items-center gap-0.5 sm:gap-1",
            trend.isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
          )}>
            <span className="text-sm sm:text-base">{trend.isPositive ? "↑" : "↓"}</span>
            <span>{Math.abs(trend.value)}%</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickStatsCard;